import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Hls from 'hls.js';
import GradedCanvas from './GradedCanvas';
import GradePanel from './GradePanel';
import StageAssist from './StageAssist';
import ReferenceMatch, { resolveGradeRefUrl } from './ReferenceMatch';
import Scopes from './Scopes';
import SplitPreview from './SplitPreview';
import ClipInspector from './ClipInspector';
import ClipFilmstrip from './ClipFilmstrip';
import { captureFrameAt } from './ClipThumb';
import { getAssetPlaybackUrl } from '../data/uploadService';
import {
  useGradePresets,
  addGradePreset,
  deleteGradePreset,
  subscribeAsset,
  requestMovieClipPreview,
  queueSdkPreview,
} from '../data/firestore';
import {
  DEFAULT_GRADE,
  clone,
  gradeSignature,
  gradeSignatureStable,
  composeGrade,
  activeLayers,
  autoGrade,
  applyLook,
  neutralLook,
  PRESETS,
  stampPreset,
} from '../utils/grade';
import {
  VIDEO_STAGES,
  gradeUpToStage,
  computeStageSuggestions,
  stageSuggestionsByPath,
  applyPathSuggestions,
  hasAutoSuggestions,
} from '../utils/gradeStages';
import { captureCanvasFrame, measurementLines, MATCH_REF_STAGE } from '../utils/stageAssist';
import { fmtDuration, useNow } from '../utils/eta';
import { fmtTime, clipSpeed, sdkPreviewFor, effectiveRedDecode } from '../utils/movie';
import { httpsify } from '../utils/url';

// WebGL render cap per pane. 1600 lets the 1080p edit proxy actually resolve
// on hi-DPI screens (split panes render ~650-800 CSS px ≈ 1300-1600 device px);
// single view uses 1920 (see paneW).
const SIDE_W = 1600;

// Auto-HD frame cache: raw full-res canvases keyed by clip+source-time. The
// parked frame plus a filmstrip's worth of prefetched spots (up to 12) must
// fit; ~8 MB per 1920px frame → ~145 MB ceiling, evicted FIFO.
const HIRES_CACHE_MAX = 18;
const IDENTITY_SIG = gradeSignature(DEFAULT_GRADE);
// True passthrough of the source. DEFAULT_GRADE looks "identity" by signature
// (so the renderer SKIPS it for ungraded clips → published raw) but its
// `adobe-color` profile still bakes a contrast S-curve in the WebGL preview.
// Showing that for an untouched clip makes the color stage look darker than the
// Assemble stage (raw <video>) and than the actual published file. RAW_GRADE
// drops the profile so "Original (raw)" — and any clip with no active grade —
// matches the raw source byte-for-byte.
const RAW_GRADE = { ...clone(DEFAULT_GRADE), profile: 'neutral' };

// Live, clip-aware color/light studio for the movie Editor's "Color & Light"
// tab. It plays the movie's clips directly from their own sources (RED HLS
// proxies or presigned MP4s), switching the <video> source as the shared
// timeline playhead crosses clip boundaries — so grading is fully interactive
// with ZERO precompose / master render. Cross-dissolves are shown as hard cuts
// here (the true composited transitions are reviewed in the explicit preview).
//
// The grade panel targets the clip under the playhead by default ("This clip"),
// which is the per-clip look baked by the renderer; switch to "Whole movie" to
// edit the global look applied on top. Single-grade preview shows whichever
// layer you're editing.
//
//   clips         ordered movie clips (with .grade / .gradeAssetId / .url / .s3Key)
//   movieGrade    the movie-wide grade
//   onClipGrade   (clipId, grade) => void   — persist a per-clip look (debounced by parent)
//   onMovieGrade  (grade) => void           — persist the movie-wide look (debounced by parent)
//   onTime        (movieSec) => void         — report playhead to the shared timeline
//   seekRef       ref whose .current is set to seekMovie(movieSec)
//   footer        node rendered under the stage (the shared timeline)
export default function LiveGradeStudio({
  clips = [],
  movieGrade,
  onClipGrade,
  onMovieGrade,
  assetLooks = {},
  onApplyAssetLook,
  onClearClipLook,
  onClipSpeed,
  onTime,
  seekRef,
  ctlRef,
  onPlayingChange,
  footer,
  movieId = '',
  sdkPreviews = null,
  movieOutput = null,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  initialTime = 0,
  initialPlaying = false,
  sideHidden = false,
  onToggleSide,
}) {
  const { docs: presets } = useGradePresets();

  const videoRef = useRef(null);
  const [videoEl, setVideoEl] = useState(null);
  const [stats, setStats] = useState(null);
  const [origStats, setOrigStats] = useState(null);
  // No autoplay-on-entry: the transport state carries over from the previous
  // tab (paused stays paused, playing resumes) — see initialPlaying.
  const [playing, setPlaying] = useState(!!initialPlaying);
  const [err, setErr] = useState('');
  const [layers, setLayers] = useState({ light: true, color: true, effects: true });
  const [viewMode, setViewMode] = useState('split'); // 'split' | 'single'
  const [showStrip, setShowStrip] = useState(false); // per-clip grade contact sheet
  const [fullCheck, setFullCheck] = useState(null); // { canvas, rawUrl, t, clipId } full-res grab
  const [fullBusy, setFullBusy] = useState(false);
  const [fullErr, setFullErr] = useState('');
  const [hiRes, setHiRes] = useState(null); // { canvas, key } — auto full-res frame while parked
  const [hiResBusy, setHiResBusy] = useState(false);
  const hiResCache = useRef(new Map()); // key → canvas (FIFO, raw frames — grade applies live)
  const [stripTimes, setStripTimes] = useState(null); // { clipId, times[] } — filmstrip sample spots
  const [hqPrev, setHqPrev] = useState(null); // asset.moviePreview — baked HQ segment result
  const [hqRender, setHqRender] = useState(null); // asset.clipGradeRender — live job status
  const [hqBusy, setHqBusy] = useState(false);
  const [hqErr, setHqErr] = useState('');
  const [hqOpen, setHqOpen] = useState(false);
  const [hqLogOpen, setHqLogOpen] = useState(false); // expandable worker log in the HQ panel
  const [target, setTarget] = useState('clip'); // 'clip' | 'movie'
  const [panelMode, setPanelMode] = useState('quick'); // 'quick' | 'stages'
  const [stageIdx, setStageIdx] = useState(0); // index into VIDEO_STAGES
  const [stageStats, setStageStats] = useState(null); // histogram of the stage INPUT pane → auto suggestions
  const [t, setT] = useState(Math.max(0, initialTime || 0)); // movie time
  const [gradeRef, setGradeRef] = useState(null); // asset.gradeRef — reference-look frame pairing
  const [urls, setUrls] = useState({});
  // clipId → source-second of the loaded file's t=0. Non-zero only for SDK
  // preview proxies (clip-window MP4s): video.currentTime = srcSec - off.
  const [srcOffs, setSrcOffs] = useState({});
  const [measured, setMeasured] = useState({});
  // Tab switches remount the studio; start on the clip under the carried-over
  // playhead (initialTime) instead of clip 0. The first metadata load then
  // lands the video at that movie time (see initialSeekRef in the load effect).
  const [segIndex, setSegIndex] = useState(() => {
    const t0 = Math.max(0, initialTime || 0);
    if (!t0) return 0;
    let acc = 0;
    for (let i = 0; i < clips.length; i += 1) {
      const c = clips[i];
      const out = c.outSec && c.outSec > 0 ? c.outSec : c.sourceDurationSec || 0;
      const len = Math.max(0, (out || 0) - (c.inSec || 0)) / clipSpeed(c);
      if (t0 < acc + len) return i;
      acc += len;
    }
    return acc > 0 ? Math.max(0, clips.length - 1) : 0;
  });
  // One-shot: the movie time the FIRST clip load should land on (instead of
  // the clip's in-point). Consumed by the load effect's onMeta below.
  const initialSeekRef = useRef(initialTime > 0 ? initialTime : null);

  const playingRef = useRef(!!initialPlaying);
  // One-shot: the first clip load's auto-resume is policy-guarded — if play()
  // rejects there, drop to a paused UI (mid-session rejections stay silent:
  // they're usually just an aborted load during a quick clip switch).
  const initialResumeRef = useRef(!!initialPlaying);
  const segRef = useRef(0);
  useEffect(() => { playingRef.current = playing; }, [playing]);
  useEffect(() => { segRef.current = segIndex; }, [segIndex]);

  // Timeline segments: each clip's movie-time start + length (after speed).
  const segments = useMemo(() => {
    let acc = 0;
    return clips.map((c) => {
      const out = c.outSec && c.outSec > 0 ? c.outSec : measured[c.id] || c.sourceDurationSec || 0;
      const speed = clipSpeed(c);
      const srcLen = Math.max(0, (out || 0) - (c.inSec || 0));
      const len = srcLen / speed;
      const seg = { ...c, outEff: out, speed, srcLen, len, startInMovie: acc };
      acc += len;
      return seg;
    });
  }, [clips, measured]);
  const totalDur = useMemo(() => segments.reduce((s, c) => s + c.len, 0), [segments]);

  // Resolve playback URLs (CDN url, else presign the raw key).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = {};
      const offs = {};
      for (const c of clips) {
        // A matching SDK preview proxy wins outright: the clip's used window
        // decoded through its chosen RED SDK development (movie.sdkPreviews,
        // built by the frameProofs worker) — the panes then show the TRUE
        // de-log the bake will use, not flat log + a WebGL approximation. The
        // file's t=0 is source second startSec; srcOffs carries the offset.
        const sp = sdkPreviewFor(sdkPreviews, c, movieOutput);
        if (sp) {
          next[c.id] = httpsify(sp.url);
          if (sp.startSec > 0) offs[c.id] = sp.startSec;
          continue;
        }
        // Prefer the lightweight 720p edit proxy for scrubbing — a single-frame
        // seek on the full UHD/1080p delivery HLS (4s segments, ~2s keyframes)
        // can stall for seconds on RED footage. The grade is a resolution-
        // independent LUT, so the look is unchanged; the published render still
        // uses the full-resolution source, and the parked-playhead auto-HD pull
        // covers true-detail checks.
        //
        // Short clips: the single progressive MP4 (editMp4Url) loads instantly.
        // Long clips: that same MP4 can be tens of MB (e.g. a 25-min clip ≈ 80MB)
        // and a bare <video> must buffer far into it before it can seek — so for
        // long clips prefer the SEGMENTED 1080p HLS edit proxy (editUrl), which
        // streams only the window you're viewing. Falls back to the delivery proxy.
        const dur = c.sourceDurationSec || ((c.outSec || 0) - (c.inSec || 0)) || 0;
        const longClip = dur > 150;
        const src = longClip
          ? (c.editUrl || c.editMp4Url || c.url)
          : (c.editMp4Url || c.editUrl || c.url);
        if (src) next[c.id] = httpsify(src);
        else if (c.s3Key) {
          try {
            // eslint-disable-next-line no-await-in-loop
            next[c.id] = await getAssetPlaybackUrl(c.s3Key);
          } catch { next[c.id] = ''; }
        }
      }
      if (!cancelled) { setUrls(next); setSrcOffs(offs); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clips.map((c) => `${c.id}|${c.editUrl || ''}|${c.editMp4Url || ''}|${c.url || ''}|${c.s3Key || ''}|${sdkPreviews?.[c.id]?.url || ''}|${JSON.stringify(c.redDecode || null)}|${c.grade?.inputTransform || ''}|${c.inSec || 0}|${c.outSec || 0}`).join('~') + `|${movieOutput?.redPipeline || ''}|${movieOutput?.redExposure ?? ''}`]);

  const curSeg = segments[segIndex];
  const curUrl = curSeg ? urls[curSeg.id] : '';
  const curOff = curSeg ? srcOffs[curSeg.id] || 0 : 0;
  const srcOffsRef = useRef({});
  srcOffsRef.current = srcOffs;
  const offFor = (id) => srcOffsRef.current[id] || 0;
  // Mutable view of the active segment + all segments, read inside player
  // handlers so grade tweaks (which rebuild `segments`) don't churn effect deps
  // and reload the <video>. The effects key off stable ids/urls only.
  const curSegRef = useRef(null);
  curSegRef.current = curSeg;
  const segmentsRef = useRef(segments);
  segmentsRef.current = segments;
  const curClipId = curSeg ? curSeg.id : '';

  // SDK preview state for the ACTIVE clip: playing one / could build one.
  const curSdkPrev = curSeg ? sdkPreviewFor(sdkPreviews, curSeg, movieOutput) : null;
  const curSdkEligible = !!curSeg?.isRawVideo
    && effectiveRedDecode(curSeg, movieOutput).pipeline !== 'default';
  const [sdkPrevQueued, setSdkPrevQueued] = useState(null); // clipId with a build in flight
  useEffect(() => {
    if (curSdkPrev && sdkPrevQueued === curClipId) setSdkPrevQueued(null);
  }, [curSdkPrev, sdkPrevQueued, curClipId]);
  const buildSdkPreview = async () => {
    if (!movieId || !curClipId) return;
    setSdkPrevQueued(curClipId);
    try { await queueSdkPreview(movieId, curClipId); } catch { setSdkPrevQueued(null); }
  };

  // The full-res still belongs to one clip — drop it when the playhead moves to
  // another so it can't be mistaken for the current clip.
  useEffect(() => { setFullCheck(null); setFullErr(''); }, [curClipId]);

  // Watch the active clip's ASSET doc for a baked HQ segment preview landing
  // (asset.moviePreview) + the live render-job status while it cooks.
  const curTripId = curSeg ? curSeg.tripId || '' : '';
  const curAssetId = curSeg ? curSeg.assetId || '' : '';
  useEffect(() => {
    setHqErr('');
    setHqOpen(false);
    setHqLogOpen(false);
    if (!curTripId || !curAssetId) { setHqPrev(null); setHqRender(null); setGradeRef(null); return undefined; }
    const unsub = subscribeAsset(curTripId, curAssetId, (a) => {
      setHqPrev(a?.moviePreview || null);
      setHqRender(a?.clipGradeRender || null);
      setGradeRef(a?.gradeRef || null);
    });
    return () => unsub();
  }, [curTripId, curAssetId]);

  // Attach the current clip's source (hls.js for .m3u8, native otherwise) and
  // seek to its in-point once metadata is in.
  useEffect(() => {
    const video = videoRef.current;
    const seg = curSegRef.current;
    if (!video || !seg || !curUrl) return undefined;
    setErr('');
    const onMeta = () => {
      const s = curSegRef.current;
      if ((!s.outSec || s.outSec <= 0) && Number.isFinite(video.duration)) {
        setMeasured((m) => (m[s.id] ? m : { ...m, [s.id]: video.duration }));
      }
      // First load after a tab switch: land at the carried-over movie time
      // (clamped inside this clip) instead of the clip's in-point.
      const pending = initialSeekRef.current;
      initialSeekRef.current = null;
      let landAt = s.inSec || 0;
      if (pending != null) {
        const local = (s.inSec || 0) + Math.max(0, pending - (s.startInMovie || 0)) * (s.speed || 1);
        // Stay a beat inside the out-point so the advance handler doesn't
        // immediately hop to the next clip on landing.
        const outEff = s.outEff && s.outEff > 0 ? s.outEff - 0.1 : Infinity;
        landAt = Math.max(landAt, Math.min(local, outEff));
      }
      try { video.currentTime = Math.max(0, landAt - offFor(s.id)); } catch { /* noop */ }
      try { video.playbackRate = s.speed || 1; } catch { /* noop */ }
      if (playingRef.current) {
        const wasInitialResume = initialResumeRef.current;
        video.play().catch(() => { if (wasInitialResume) setPlaying(false); });
      }
      initialResumeRef.current = false;
    };
    video.addEventListener('loadedmetadata', onMeta);

    // Detect from the URL we actually loaded: an .mp4 edit proxy must NOT go
    // through hls.js (which would fail), even though the clip's streamType is
    // 'hls' for its delivery proxy. Fall back to streamType only for extension-
    // less URLs (e.g. a presigned s3Key).
    const isHls = /\.m3u8(\?|$)/.test(curUrl)
      || (!/\.mp4(\?|$)/.test(curUrl) && seg.streamType === 'hls');
    let hls;
    // Prefer hls.js whenever it's supported (Chrome/Firefox). We do NOT gate on
    // video.canPlayType('application/vnd.apple.mpegurl') — some Chrome builds
    // report "maybe" yet can't actually decode an .m3u8, which silently skipped
    // hls.js and left a black preview. Safari (Hls.isSupported() === false)
    // falls through to native HLS via video.src. Also: no capLevelToPlayerSize —
    // the <video> is display:none (0×0), so capping to player size starves it to
    // no rendition and nothing decodes. (Matches the working single-clip studio.)
    if (isHls && Hls.isSupported()) {
      hls = new Hls({ enableWorker: true, lowLatencyMode: false });
      hls.loadSource(curUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (!data.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
        else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
        else setErr('Could not load this clip for grading.');
      });
    } else {
      video.src = curUrl;
      video.load();
    }
    return () => {
      video.removeEventListener('loadedmetadata', onMeta);
      if (hls) hls.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curClipId, curUrl]);

  // Recover an expired presigned MP4 link (raw clips) without losing position.
  useEffect(() => {
    const video = videoRef.current;
    const seg = curSegRef.current;
    if (!video || !seg) return undefined;
    const isHls = seg.streamType === 'hls' || (curUrl && /\.m3u8(\?|$)/.test(curUrl));
    if (isHls || !seg.s3Key) return undefined;
    let recovering = false;
    const recover = async () => {
      if (recovering) return;
      recovering = true;
      const at = video.currentTime;
      const wasPlaying = playingRef.current;
      try {
        const fresh = await getAssetPlaybackUrl(seg.s3Key);
        if (fresh) {
          const onMeta = () => {
            video.removeEventListener('loadedmetadata', onMeta);
            try { if (at > 0) video.currentTime = at; video.playbackRate = curSegRef.current.speed || 1; } catch { /* noop */ }
            if (wasPlaying) video.play().catch(() => {});
          };
          video.addEventListener('loadedmetadata', onMeta);
          video.src = fresh;
          video.load();
        }
      } catch { /* surface native error if re-sign fails */ } finally {
        setTimeout(() => { recovering = false; }, 5000);
      }
    };
    video.addEventListener('error', recover);
    return () => video.removeEventListener('error', recover);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curClipId, curUrl]);

  // Wire the <video> into the canvases once it can render.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const onReady = () => setVideoEl(v);
    v.addEventListener('loadeddata', onReady);
    if (v.readyState >= 2) onReady();
    return () => v.removeEventListener('loadeddata', onReady);
  }, []);

  // Retime the live preview immediately when the active clip's speed changes
  // (the inspector's speed control), without reloading the source.
  const curSpeed = curSeg ? curSeg.speed : 1;
  useEffect(() => {
    const v = videoRef.current;
    if (v) { try { v.playbackRate = curSpeed || 1; } catch { /* noop */ } }
  }, [curSpeed, curClipId]);

  // Advance to the next clip at the current clip's out-point.
  const segCount = segments.length;
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const onTimeUpdate = () => {
      const seg = curSegRef.current;
      if (!seg) return;
      const outEff = seg.outEff && seg.outEff > 0 ? seg.outEff : Infinity;
      if (video.currentTime + offFor(seg.id) >= outEff - 0.05) {
        if (segRef.current < segCount - 1) setSegIndex(segRef.current + 1);
        else { video.pause(); setPlaying(false); }
      }
    };
    const onEnded = () => {
      if (segRef.current < segCount - 1) setSegIndex(segRef.current + 1);
      else setPlaying(false);
    };
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('ended', onEnded);
    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('ended', onEnded);
    };
  }, [curClipId, segCount]);

  // Movie clock: compute movie-time from the active segment + report it.
  useEffect(() => {
    let raf;
    let lastUi = 0;
    const loop = (ts) => {
      const video = videoRef.current;
      const seg = segmentsRef.current[segRef.current];
      let time = 0;
      if (video && seg) time = seg.startInMovie + Math.max(0, video.currentTime + offFor(seg.id) - (seg.inSec || 0)) / (seg.speed || 1);
      if (ts - lastUi > 80) {
        lastUi = ts;
        setT(time);
        if (onTime) onTime(time);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [onTime]);

  // Seek by movie time (shared timeline click-to-seek + internal scrubber).
  const seekMovie = useCallback((sec) => {
    const target = Math.max(0, Math.min(sec, totalDur));
    let idx = segments.findIndex((s) => target < s.startInMovie + s.len);
    if (idx < 0) idx = segments.length - 1;
    const seg = segments[idx];
    if (!seg) return;
    // The loaded file's t=0 may be offset into the source (SDK preview proxies).
    const local = Math.max(0, (seg.inSec || 0) + (target - seg.startInMovie) * (seg.speed || 1) - offFor(seg.id));
    setT(target);
    const v = videoRef.current;
    if (idx !== segRef.current) {
      setSegIndex(idx);
      const onceMeta = () => {
        try { v.currentTime = local; } catch { /* noop */ }
        v.removeEventListener('loadedmetadata', onceMeta);
      };
      v?.addEventListener('loadedmetadata', onceMeta);
    } else if (v) {
      try { v.currentTime = local; } catch { /* noop */ }
    }
  }, [segments, totalDur]);

  useEffect(() => {
    if (!seekRef) return undefined;
    seekRef.current = seekMovie;
    return () => { if (seekRef) seekRef.current = null; };
  }, [seekRef, seekMovie]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  const pauseCtl = useCallback(() => {
    const v = videoRef.current;
    if (v) v.pause();
    setPlaying(false);
  }, []);

  useEffect(() => {
    if (onPlayingChange) onPlayingChange(playing);
  }, [playing, onPlayingChange]);
  useEffect(() => {
    if (!ctlRef) return undefined;
    ctlRef.current = { toggle: togglePlay, pause: pauseCtl };
    return () => { if (ctlRef) ctlRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctlRef, pauseCtl]);

  // The grade being edited: the active clip's look, or the movie-wide look.
  const activeClip = clips[segIndex] || clips[0] || null;
  // RED provenance for the decode stage. Clips added recently carry the stamped
  // isRawVideo flag; legacy clips (added before the stamp existed) are sniffed
  // by their .RDC/.R3D source name so the Decode controls never silently hide.
  const isRedClip = !!activeClip
    && (!!activeClip.isRawVideo || /\.(r3d|rdc)(\/|$)/i.test(activeClip.label || activeClip.name || ''));
  const clipGrade = useMemo(
    () => (activeClip && activeClip.grade ? activeClip.grade : DEFAULT_GRADE),
    [activeClip],
  );
  const movieG = movieGrade || DEFAULT_GRADE;
  const editing = target === 'movie' ? movieG : clipGrade;

  const commit = (next) => {
    if (target === 'movie') onMovieGrade(next);
    else if (activeClip) onClipGrade(activeClip.id, next);
  };

  const compositeGrade = useMemo(() => composeGrade(editing, layers), [editing, layers]);
  const live = useMemo(() => activeLayers(editing), [editing]);

  // The renderer bakes BOTH the per-clip look and the movie-wide look (clip
  // first, movie on top). Preview that exact composite so the studio is WYSIWYG
  // with the published file. While editing one layer (with the A/B layer chips),
  // the OTHER layer is applied in full underneath/over, mirroring publish order.
  const otherGrade = target === 'movie' ? clipGrade : movieG;
  const otherActive = gradeSignature(otherGrade) !== IDENTITY_SIG;
  // The renderer only bakes a layer when it's *active*; an untouched clip is
  // published raw. Mirror that: if neither the clip nor the movie look is doing
  // anything, preview the raw source so the studio == Assemble == published.
  const editingActive = gradeSignature(compositeGrade) !== IDENTITY_SIG;
  const publishedActive = editingActive || otherActive;
  const paneGrade = !publishedActive
    ? RAW_GRADE
    : target === 'clip' && otherActive
      ? movieG
      : compositeGrade;
  const paneBase = !publishedActive
    ? undefined
    : target === 'clip' && otherActive
      ? compositeGrade
      : target === 'movie' && otherActive
        ? clipGrade
        : undefined;

  // ---- Guided stage-by-stage workflow (same pipeline as the photo editor,
  // minus local masks — per-pixel spatial masks are the one step too heavy to
  // bake across thousands of video frames). Left pane = the grade THROUGH the
  // previous stage, right = that plus this stage's edits, so each step shows
  // exactly what it adds. The stage-input histogram powers per-slider auto
  // suggestions, identical to the photo workflow.
  const stageMode = panelMode === 'stages';
  const stageDef = VIDEO_STAGES[stageIdx] || VIDEO_STAGES[0];
  // WYSIWYG normalization for the stage panes: a cumulative sub-grade that is
  // identity-by-signature is SKIPPED by the renderer (published raw), but
  // DEFAULT_GRADE's 'adobe-color' profile would still bake a contrast S-curve
  // into the WebGL preview — so a freshly reset stage kept showing a "color
  // transformed" right pane. Map identity → RAW passthrough, same as quick mode.
  const rawIfIdentity = (g) => (gradeSignature(g) === IDENTITY_SIG ? RAW_GRADE : g);
  const stageBefore = useMemo(
    () => (stageIdx === 0 ? RAW_GRADE : rawIfIdentity(gradeUpToStage(editing, stageIdx - 1, VIDEO_STAGES))),
    [editing, stageIdx],
  );
  const stageAfter = useMemo(
    () => rawIfIdentity(gradeUpToStage(editing, stageIdx, VIDEO_STAGES)),
    [editing, stageIdx],
  );
  // Decode-stage context: the current transform, whether the source is RED RAW
  // (suggest the Log3G10 de-log even before it's set), and the video output
  // convention (Rec.709 — sRGB is for stills / frame grabs).
  const stageSug = stageMode
    ? computeStageSuggestions(stageStats, {
      inputTransform: editing?.inputTransform,
      isRed: isRedClip,
      kind: 'video',
    })
    : null;
  const stageSugByPath = stageMode ? stageSuggestionsByPath(stageDef, stageSug) : null;
  // Which stages are currently doing something — the "adjustment tree" review.
  // A stage is live when clearing (just) its fields changes the grade.
  const stageLive = useMemo(() => VIDEO_STAGES.map((s) => {
    const cleared = clone(editing);
    s.clear(cleared);
    return gradeSignature(cleared) !== gradeSignature(editing);
  }), [editing]);
  // Apply every measured suggestion for this stage in one shot. Guidance/taste
  // entries (skipAuto) stay chip-only so the bulk apply never wipes a deliberate
  // tweak with a "leave neutral" recommendation.
  const applyStageSug = () => {
    const next = applyPathSuggestions(editing, stageSugByPath);
    if (next) commit(next);
  };
  const stageHasSug = hasAutoSuggestions(stageSugByPath);
  // Undo everything the CURRENT stage is doing (the stage's clear() — the same
  // fields the live-dot check watches), leaving every other stage untouched.
  const resetStage = () => {
    const next = clone(editing);
    stageDef.clear(next);
    commit(next);
  };

  // The 🎯 Match-reference pass captures RAW vs the FULL clip grade (not the
  // stage-truncated panes) — this flag flips both panes for the capture window.
  const [matchCap, setMatchCap] = useState(false);

  // What the split view shows: normally the WYSIWYG published composite; in
  // stage mode the focused before/after of just the current stage.
  const showGrade = stageMode ? (matchCap ? rawIfIdentity(editing) : stageAfter) : paneGrade;
  const showBase = stageMode ? undefined : paneBase;
  const leftGrade = stageMode ? (matchCap ? RAW_GRADE : stageBefore) : RAW_GRADE;
  const leftLabel = stageMode
    ? (stageIdx === 0 ? 'Original (raw)' : `Through ${VIDEO_STAGES[stageIdx - 1].name}`)
    : 'Original (raw)';
  const rightLabelBase = stageMode
    ? `+ ${stageDef.name}`
    : otherActive
      ? 'As published · clip + movie'
      : (target === 'movie' ? 'Whole-movie look' : 'This clip');
  // Parked-playhead HD indicator on the graded pane's label — text-only, so the
  // proxy → full-res swap never shifts the layout.
  const hdTag = !playing ? (hiRes ? ' · ◉ HD' : hiResBusy ? ' · ◉ HD…' : '') : '';
  const rightLabel = `${rightLabelBase}${hdTag}`;

  // ONE shared sizing style for both panes so raw/graded can never render at
  // different scales. The canvas fills its pane box and object-fit:contain
  // letterboxes the frame inside it — the image keeps the source aspect ratio
  // no matter how the responsive layout shapes the pane (no vertical squash /
  // crop against the stage's overflow:hidden). Where the pane height chain is
  // indefinite, height:100% resolves to auto and width drives the aspect.
  const paneStyle = { width: '100%', height: '100%', objectFit: 'contain', display: 'block' };
  const paneW = (big) => (big ? 1920 : SIDE_W);

  const layerRows = [
    { key: 'light', label: 'Light', hint: 'Exposure, contrast, tone', live: live.light },
    { key: 'color', label: 'Color', hint: 'WB, vibrance, color mixer, grading', live: live.color },
    { key: 'effects', label: 'Effects', hint: 'Clarity, vignette, grain, sharpen', live: live.effects },
  ];

  const leftPane = (big) => (
    videoEl ? (
      <GradedCanvas
        source={(!playing && hiRes) ? hiRes.canvas : videoEl}
        grade={leftGrade}
        onStats={stageMode ? setStageStats : setOrigStats}
        onError={setErr}
        maxWidth={paneW(big)}
        style={paneStyle}
        className="cs-cap-lgs-before"
      />
    ) : (
      <div className="cs-studio-ph">
        <span className="cs-spinner" /><strong>Original</strong>
        <small>{curUrl ? 'loading the clip…' : 'no playable source for this clip'}</small>
      </div>
    )
  );
  // Grab the current frame at FULL resolution from the clip's master-quality
  // delivery source (not the light 1080p scrub proxy) so the user can confirm the
  // grade at true detail. The live preview stays fast; this is on-demand.
  const grabFullRes = async () => {
    const seg = curSegRef.current;
    if (!seg) return;
    setFullBusy(true);
    setFullErr('');
    try {
      let url = seg.url ? httpsify(seg.url) : '';
      if (!url && seg.s3Key) url = await getAssetPlaybackUrl(seg.s3Key);
      if (!url) throw new Error('no full-res source');
      const srcSec = (seg.inSec || 0) + Math.max(0, t - (seg.startInMovie || 0)) * (seg.speed || 1);
      const f = await captureFrameAt({ url, streamType: seg.streamType, atSec: srcSec, maxW: 1920 });
      setFullCheck({ ...f, t, clipId: seg.id });
    } catch {
      setFullErr('Couldn’t grab a full-res frame for this clip.');
    } finally {
      setFullBusy(false);
    }
  };

  // Reference-look frames for the stage assist: pull the paired moments out of
  // the reference clip (photog's graded delivery), cached per asset+time — the
  // decoder is torn down after each grab, so only small JPEG data-URLs stay.
  const gradeRefRef = useRef(null);
  gradeRefRef.current = gradeRef;
  const refUrlCache = useRef({}); // refAssetId → resolved playable URL
  const refFrameCache = useRef(new Map()); // `${refAssetId}|${refT}` → dataUrl
  const grabReferenceFrames = async (refTs) => {
    const gr = gradeRefRef.current;
    if (!gr || !refTs.length) return null;
    const rid = gr.refAssetId || 'ref';
    let url = refUrlCache.current[rid];
    if (url == null) {
      url = await resolveGradeRefUrl(gr).catch(() => '');
      refUrlCache.current = { [rid]: url }; // single-entry cache (presigns expire per-session anyway)
    }
    if (!url) return null;
    const frames = [];
    for (const rt of refTs) {
      const key = `${rid}|${rt.toFixed(2)}`;
      let dataUrl = refFrameCache.current.get(key);
      if (!dataUrl) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const f = await captureFrameAt({ url, atSec: rt, maxW: 1024 });
          dataUrl = f.rawUrl || '';
        } catch { dataUrl = ''; }
        if (dataUrl) {
          refFrameCache.current.set(key, dataUrl);
          if (refFrameCache.current.size > 12) {
            refFrameCache.current.delete(refFrameCache.current.keys().next().value);
          }
        }
      }
      if (dataUrl) frames.push({ label: `reference @ ${fmtTime(rt)}`, dataUrl });
    }
    return frames.length ? frames : null;
  };

  // Multi-frame capture for the Grok stage assist: sample BOTH stage panes
  // (left = stage input grade, right = current output grade) at the playhead
  // PLUS spots spread across the active clip's trimmed window (~15/50/85%), by
  // seeking the live proxy <video> — both WebGL panes repaint per seek — then
  // restoring the playhead. One grade covers the whole clip, so Grok should see
  // moments where the surfer is actually in frame, not just one empty-ocean
  // frame. n=1 skips all seeking (current panes as-is).
  //
  // When a reference pairing exists (asset.gradeRef, built in ReferenceMatch),
  // the samples are taken AT the paired keyframes instead of the generic
  // spread, and each one's matching reference frame rides along — so Grok
  // judges "this exact moment, raw vs current vs the photog's grade of it".
  const captureStageFrames = async (n = 1) => {
    const seg = curSegRef.current;
    const v = videoRef.current;
    const snap = () => ({
      before: captureCanvasFrame('.cs-cap-lgs-before'),
      after: captureCanvasFrame('.cs-cap-lgs-after'),
    });
    const start = seg?.startInMovie || 0;
    const len = seg?.len || 0;
    const label = (movieSec) => `${fmtTime(Math.max(0, movieSec - start))} / ${fmtTime(len)}`;
    const nowT = () => (seg && v
      ? start + Math.max(0, v.currentTime - (seg.inSec || 0)) / (seg.speed || 1)
      : t);
    // Valid saved pairs, mapped into movie time (pair times are source secs).
    const clampT = (sec) => Math.min(Math.max(sec, start), start + Math.max(0.05, len - 0.05));
    const pairs = (Array.isArray(gradeRefRef.current?.pairs) ? gradeRefRef.current.pairs : [])
      .filter((p) => Number.isFinite(p?.t) && Number.isFinite(p?.refT))
      .map((p) => ({ movieSec: clampT(start + (p.t - (seg?.inSec || 0)) / (seg?.speed || 1)), refT: p.refT }))
      .slice(0, 4);
    const single = async () => {
      const s = snap();
      if (!s.before || !s.after) return null;
      const l = label(nowT());
      // Single frame + a reference: ride the nearest pair's reference along.
      let referenceFrames = null;
      if (pairs.length) {
        const here = nowT();
        const nearest = pairs.reduce((a, b) => (Math.abs(b.movieSec - here) < Math.abs(a.movieSec - here) ? b : a));
        referenceFrames = await grabReferenceFrames([nearest.refT]);
      }
      return {
        inputFrames: [{ label: l, dataUrl: s.before }],
        outputFrames: [{ label: l, dataUrl: s.after }],
        ...(referenceFrames ? { referenceFrames } : {}),
      };
    };
    // Too short to sample meaningfully distinct moments — one frame is honest.
    if (!seg || !v || n <= 1 || !(len > 1.2)) return single();

    if (!v.paused) { v.pause(); setPlaying(false); }
    const t0 = clampT(nowT());
    let times;
    let refTs = null;
    if (pairs.length) {
      // Paired mode: sample exactly the matched keyframes so input/output and
      // reference frames line up index-for-index.
      const ordered = [...pairs].sort((a, b) => a.movieSec - b.movieSec).slice(0, Math.max(n, 2));
      times = ordered.map((p) => p.movieSec);
      refTs = ordered.map((p) => p.refT);
    } else {
      // Keep the playhead frame (it's what the user is looking at) and fill the
      // rest with the spread spots farthest from it, sampled in clip order.
      const spread = [0.15, 0.5, 0.85].map((f) => start + Math.min(len * f, len - 0.05));
      const picks = spread
        .filter((sec) => Math.abs(sec - t0) > Math.max(0.35, len * 0.05))
        .sort((a, b) => Math.abs(b - t0) - Math.abs(a - t0))
        .slice(0, Math.max(0, n - 1));
      times = [t0, ...picks].sort((a, b) => a - b);
    }
    const seekSettle = (sec) => new Promise((resolve) => {
      let fin = false;
      const finish = () => {
        if (fin) return;
        fin = true;
        v.removeEventListener('seeked', finish);
        // Give the WebGL panes time to upload + draw the newly decoded frame
        // (and React to swap a parked hi-res canvas back to the live video).
        requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(resolve, 200)));
      };
      v.addEventListener('seeked', finish);
      seekMovie(sec);
      setTimeout(finish, 3000);
    });
    const inputFrames = [];
    const outputFrames = [];
    const capturedRefTs = [];
    for (let i = 0; i < times.length; i += 1) {
      const sec = times[i];
      // eslint-disable-next-line no-await-in-loop
      if (Math.abs(sec - nowT()) > 0.05) await seekSettle(sec);
      const s = snap();
      if (s.before && s.after) {
        inputFrames.push({ label: label(sec), dataUrl: s.before });
        outputFrames.push({ label: label(sec), dataUrl: s.after });
        if (refTs) capturedRefTs.push(refTs[i]);
      }
    }
    await seekSettle(t0); // put the playhead back where the user left it
    if (!inputFrames.length) return null;
    const referenceFrames = capturedRefTs.length ? await grabReferenceFrames(capturedRefTs) : null;
    // Only attach the references when EVERY sample got its pair — a partial set
    // would break the by-index pairing the backend prompt promises the model.
    const paired = referenceFrames && referenceFrames.length === inputFrames.length;
    return { inputFrames, outputFrames, ...(paired ? { referenceFrames } : {}) };
  };

  // Capture for the cross-stage 🎯 Match-reference pass: same sampling as the
  // stage assist (paired keyframes + references), but with the panes flipped to
  // RAW vs the FULL clip grade so Grok judges the finished look, not a stage
  // slice. The flag flip forces a LUT recompile — give it a beat to repaint.
  const captureMatchFrames = async (n) => {
    setMatchCap(true);
    await new Promise((r) => setTimeout(r, 800));
    try {
      return await captureStageFrames(n);
    } finally {
      setMatchCap(false);
    }
  };

  // ---- Auto HD frame --------------------------------------------------------
  // The live panes scrub the light 1080p edit proxy (fast seeks, no buffering).
  // When playback is PAUSED and the playhead sits still for a moment, quietly
  // upgrade both panes to a full-res frame pulled client-side from the clip's
  // delivery source (RED UHD HLS proxy / presigned original) — the same
  // mechanism as "Full-res check", but automatic, debounced and cancellable.
  // The raw frame is cached per clip+time (the grade is applied live in WebGL,
  // so slider drags re-render the cached frame instantly); any seek or play
  // reverts to the proxy. Swapped in-place: same aspect, no layout shift.
  useEffect(() => {
    const seg = curSegRef.current;
    if (playing || !seg) { setHiRes(null); setHiResBusy(false); return undefined; }
    // An SDK preview proxy IS the color truth — a full-res frame pulled from
    // the flat-log delivery source would grade wrong over an srgb transform.
    if (offFor(seg.id) > 0) { setHiRes(null); setHiResBusy(false); return undefined; }
    const srcSec = (seg.inSec || 0) + Math.max(0, t - (seg.startInMovie || 0)) * (seg.speed || 1);
    const key = `${seg.id}@${srcSec.toFixed(2)}`;
    // A frame from another time/clip must never linger over the seeked proxy —
    // and neither must a superseded fetch's busy dot (its finally() is skipped
    // when stale, so the reset happens here at the start of every re-run).
    setHiRes((cur) => (cur && cur.key === key ? cur : null));
    setHiResBusy(false);
    const cached = hiResCache.current.get(key);
    if (cached) { setHiRes({ canvas: cached, key }); return undefined; }
    let stale = false;
    const timer = setTimeout(async () => {
      setHiResBusy(true);
      try {
        let url = seg.url ? httpsify(seg.url) : '';
        if (!url && seg.s3Key) url = await getAssetPlaybackUrl(seg.s3Key);
        if (!url || stale) return;
        const f = await captureFrameAt({ url, streamType: seg.streamType, atSec: srcSec, maxW: 1920 });
        if (stale) return;
        hiResCache.current.set(key, f.canvas);
        while (hiResCache.current.size > HIRES_CACHE_MAX) {
          hiResCache.current.delete(hiResCache.current.keys().next().value);
        }
        setHiRes({ canvas: f.canvas, key });
      } catch {
        /* best-effort: the proxy frame stays on screen */
      } finally {
        if (!stale) setHiResBusy(false);
      }
    }, 1200);
    return () => { stale = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, playing, segIndex]);

  // While parked with the frame strip open, quietly prefetch hi-res frames for
  // the strip's sampled timestamps into the same cache — clicking a strip tile
  // then shows the HD frame instantly instead of after another ~1-3 s pull.
  // Serial (one segment fetch at a time), cancelled by play/clip change, and
  // keyed exactly like the parked pull so a tile click is a guaranteed hit.
  useEffect(() => {
    const seg = curSegRef.current;
    if (playing || !seg || !stripTimes || stripTimes.clipId !== seg.id) return undefined;
    if (offFor(seg.id) > 0) return undefined; // SDK preview clips skip flat-log HD pulls
    const times = stripTimes.times || [];
    if (!times.length) return undefined;
    let stale = false;
    const timer = setTimeout(async () => {
      let url = seg.url ? httpsify(seg.url) : '';
      if (!url && seg.s3Key) {
        try { url = await getAssetPlaybackUrl(seg.s3Key); } catch { url = ''; }
      }
      if (!url || stale) return;
      for (const srcT of times) {
        if (stale) return;
        // Round-trip through movie time with the same arithmetic as the parked
        // effect so the cache key matches bit-for-bit after a tile click.
        const movieT = (seg.startInMovie || 0) + Math.max(0, srcT - (seg.inSec || 0)) / (seg.speed || 1);
        const srcSec = (seg.inSec || 0) + Math.max(0, movieT - (seg.startInMovie || 0)) * (seg.speed || 1);
        const key = `${seg.id}@${srcSec.toFixed(2)}`;
        if (hiResCache.current.has(key)) continue;
        try {
          // eslint-disable-next-line no-await-in-loop
          const f = await captureFrameAt({ url, streamType: seg.streamType, atSec: srcSec, maxW: 1920 });
          if (stale) return;
          hiResCache.current.set(key, f.canvas);
          while (hiResCache.current.size > HIRES_CACHE_MAX) {
            hiResCache.current.delete(hiResCache.current.keys().next().value);
          }
        } catch {
          /* best-effort; move on to the next spot */
        }
      }
    }, 2500); // let the parked frame's own pull win the connection first
    return () => { stale = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripTimes, playing, segIndex]);

  // Server-bake a HIGH-RES graded proxy of just this clip's trimmed segment —
  // clip look (+ keyframes) with the movie-wide look stacked on top, exactly the
  // publish order — so the edit can be reviewed at real quality before the full
  // movie bake. Grade on the fast 1080p scrub proxy / frame strip, then bake this
  // when a clip's look is settled.
  const bakeHqPreview = async () => {
    const seg = curSegRef.current;
    if (!seg) return;
    if (!seg.tripId || !seg.assetId) { setHqErr('This clip has no linked source asset to bake from.'); setHqOpen(true); return; }
    setHqBusy(true);
    setHqErr('');
    setHqOpen(true);
    try {
      const speed = seg.speed || 1;
      // Keyframe t is clip-local TIMELINE seconds; the worker samples the bake
      // in SOURCE time from the trim start, so convert through the clip speed.
      const kfs = Array.isArray(seg.gradeKeyframes) && seg.gradeKeyframes.length >= 2
        ? [...seg.gradeKeyframes]
          .filter((k) => k && k.grade)
          .sort((a, b) => (a.t || 0) - (b.t || 0))
          .map((k) => ({ t: Math.max(0, (k.t || 0) * speed), grade: k.grade }))
        : null;
      const movieActive = gradeSignature(movieG) !== IDENTITY_SIG;
      await requestMovieClipPreview(
        seg.tripId,
        seg.assetId,
        {
          clipId: seg.id,
          t0: seg.inSec || 0,
          t1: seg.outEff && seg.outEff > 0 ? seg.outEff : 0,
          grade: seg.useGrade === false ? null : (seg.grade || null),
          gradeKeyframes: kfs,
          movieGrade: movieActive ? movieG : null,
        },
        { source: 'proxy', heightTarget: 1080, quality: 'high' }
      );
    } catch {
      setHqErr('Couldn’t queue the preview bake — try again.');
    } finally {
      setHqBusy(false);
    }
  };
  const hqJobActive = !!hqRender
    && ['queued', 'rendering'].includes(hqRender.status)
    && hqRender.override?.resultTo === 'moviePreview';
  const hqReady = !!hqPrev && hqPrev.status === 'ready' && hqPrev.clipId === curClipId;
  const hqFailed = !hqJobActive && hqPrev?.status === 'error';
  // Live progress feed the worker streams onto clipGradeRender (phase / pct /
  // ETA / rolling log). ETA counts down locally between Firestore writes.
  const hqNow = useNow(hqJobActive, 1000);
  const hqLive = useMemo(() => {
    if (!hqJobActive) return null;
    const r = hqRender || {};
    const labels = {
      queued: 'Queued',
      starting: 'Starting',
      decoding: 'Decoding RED master',
      probing: 'Probing source',
      baking: 'Baking',
      uploading: 'Uploading',
    };
    const phase = r.status === 'queued' ? 'queued' : (r.phase || 'starting');
    const pct = Number.isFinite(r.pct) ? Math.max(0, Math.min(100, r.pct)) : null;
    let etaSec = null;
    if (Number.isFinite(r.etaSec)) {
      const drift = r.updatedAt ? Math.max(0, (hqNow - r.updatedAt) / 1000) : 0;
      etaSec = Math.max(0, r.etaSec - drift);
    }
    const bits = [labels[phase] || phase];
    if (pct != null && phase !== 'queued') bits.push(`${Math.round(pct)}%`);
    if (Number.isFinite(r.outSec) && r.totalSec > 0) bits.push(`${r.outSec.toFixed(1)} / ${r.totalSec.toFixed(1)}s`);
    if (r.speed > 0) bits.push(`${r.speed}× realtime`);
    if (etaSec != null) bits.push(etaSec > 1 ? `~${fmtDuration(etaSec)} left` : 'finishing up…');
    return { phase, pct, etaSec, line: bits.join(' · '), log: Array.isArray(r.log) ? r.log : [] };
  }, [hqJobActive, hqRender, hqNow]);
  // Does the baked preview still match the CURRENT look + trim? Mirrors the
  // worker's gradeSig so edits made after the bake surface as "outdated".
  // Uses the STABLE (sorted-key) signature: the worker computes its sig from
  // the Firestore round-tripped grade, whose map key order differs from the
  // object the UI built — a plain gradeSignature() mismatches on identical
  // content and permanently flags a fresh bake "outdated".
  const hqStale = useMemo(() => {
    if (!hqReady || !curSeg) return false;
    const kfs = Array.isArray(curSeg.gradeKeyframes) && curSeg.gradeKeyframes.length >= 2
      ? [...curSeg.gradeKeyframes].filter((k) => k && k.grade).sort((a, b) => (a.t || 0) - (b.t || 0))
      : null;
    const fallback = curSeg.useGrade === false ? DEFAULT_GRADE : (curSeg.grade || DEFAULT_GRADE);
    const movieActive = gradeSignature(movieG) !== IDENTITY_SIG;
    const sig = (kfs
      ? kfs.map((k) => gradeSignatureStable(k.grade)).join('|')
      : gradeSignatureStable(fallback)) + (movieActive ? `|m:${gradeSignatureStable(movieG)}` : '');
    if (hqPrev.gradeSig !== sig) return true;
    const trimMoved = Math.abs((hqPrev.segStart || 0) - (curSeg.inSec || 0)) > 0.05
      || (Number(hqPrev.segDur) > 0 && Math.abs(hqPrev.segDur - curSeg.srcLen) > 0.05);
    return trimMoved;
  }, [hqReady, hqPrev, curSeg, movieG]);

  const rightPane = (big) => (
    videoEl ? (
      <GradedCanvas source={(!playing && hiRes) ? hiRes.canvas : videoEl} grade={showGrade} baseGrade={showBase} onStats={setStats} onError={setErr} maxWidth={paneW(big)} style={paneStyle} className="cs-cap-lgs-after" />
    ) : (
      <div className="cs-studio-ph"><span className="cs-spinner" /><strong>Graded</strong><small>your look renders here</small></div>
    )
  );

  if (!clips.length) {
    return (
      <div className="cs-section"><div className="cs-empty">Add clips to this movie first — then grade each one here, live.</div></div>
    );
  }

  const clipLabel = activeClip ? (activeClip.name || activeClip.title || `Clip ${segIndex + 1}`) : '';

  return (
    <div className={`cs-studio${sideHidden ? ' cs-side-off' : ''}`}>
      <div className="cs-studio-main">
        <video
          ref={videoRef}
          crossOrigin="anonymous"
          muted
          playsInline
          preload="auto"
          style={{ display: 'none' }}
        />
        <div className="cs-livegrade-banner">
          <span className="cs-badge cs-badge-live">● Live</span>
          {curSdkPrev && (
            <span
              className="cs-badge"
              style={{ background: 'var(--surf)', color: '#08222a' }}
              title={`This preview was decoded through the clip's RED SDK development — the color you see is the color the bake produces. ${curSdkPrev.redDecode?.pipeline || ''}${Number(curSdkPrev.redDecode?.exposure) ? ` · ${curSdkPrev.redDecode.exposure > 0 ? '+' : ''}${curSdkPrev.redDecode.exposure} EV` : ''}${curSdkPrev.redDecode?.toneMap ? ` · tone ${curSdkPrev.redDecode.toneMap}` : ''}${curSdkPrev.redDecode?.rolloff ? ` · roll-off ${curSdkPrev.redDecode.rolloff}` : ''}`}
            >
              ◆ SDK de-log
            </span>
          )}
          <span style={{ flex: '1 1 auto' }}>
            Grading <strong>clip {segIndex + 1}/{clips.length}</strong>
            {clipLabel ? ` · ${clipLabel}` : ''} straight from its source — no render needed.
            Cross-dissolves show as cuts here; build a preview to review transitions.
          </span>
        </div>
        <div className={`cs-studio-stage${viewMode === 'single' ? ' single' : ''}`}>
          {err ? (
            <div className="cs-empty">{err}</div>
          ) : viewMode === 'single' ? (
            <div className="cs-studio-single">
              <div className="cs-split-label" style={{ background: 'var(--surf)' }}>Graded{hdTag}</div>
              {rightPane(true)}
            </div>
          ) : (
            <SplitPreview
              panes={[
                { label: leftLabel, color: '#888' },
                { label: rightLabel, color: 'var(--surf)' },
              ]}
            >
              {leftPane(false)}
              {rightPane(false)}
            </SplitPreview>
          )}
        </div>

        {(fullCheck || fullErr) && (
          <div className="cs-fullcheck">
            <div className="cs-fullcheck-head">
              <span className="cs-badge">Full-resolution check</span>
              {fullCheck && (
                <span className="cs-help">
                  Frame @ {fmtTime(fullCheck.t)} · {fullCheck.w}×{fullCheck.h} · live grade applied to the true
                  master source. Tweak sliders and it updates instantly.
                </span>
              )}
              {fullErr && <span className="cs-help" style={{ color: 'var(--accent)' }}>{fullErr}</span>}
              <button
                className="cs-btn cs-btn-ghost cs-btn-sm"
                style={{ marginLeft: 'auto' }}
                onClick={grabFullRes}
                disabled={fullBusy}
              >
                {fullBusy ? 'Grabbing…' : '↻ Refresh'}
              </button>
              <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => { setFullCheck(null); setFullErr(''); }}>✕ Close</button>
            </div>
            {fullCheck && (
              <div className="cs-fullcheck-grid">
                <figure>
                  {fullCheck.rawUrl && <img src={fullCheck.rawUrl} alt="raw" draggable={false} />}
                  <figcaption>Raw</figcaption>
                </figure>
                <figure>
                  <GradedCanvas source={fullCheck.canvas} grade={showGrade} baseGrade={showBase} maxWidth={1920} style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 6 }} />
                  <figcaption>Graded</figcaption>
                </figure>
              </div>
            )}
          </div>
        )}

        {hqOpen && (
          <div className="cs-fullcheck">
            <div className="cs-fullcheck-head">
              <span className="cs-badge">HQ segment preview</span>
              {hqJobActive && (
                <span className="cs-help">
                  <span className="cs-spinner" /> {hqLive?.line || 'Baking…'} — the finished video lands
                  here automatically.
                </span>
              )}
              {hqReady && !hqJobActive && (
                <span className="cs-help">
                  {hqPrev.width && hqPrev.height ? `${hqPrev.width}×${hqPrev.height}` : ''}
                  {Number(hqPrev.segDur) > 0 ? ` · ${fmtTime(hqPrev.segDur)}` : ''}
                  {hqPrev.segments > 1 ? ` · ${hqPrev.segments} grade segments` : ''}
                  {' · clip + movie look baked, publish order'}
                  {hqStale && <b style={{ color: 'var(--accent)' }}> · outdated — the look or trim changed since this bake</b>}
                </span>
              )}
              {hqFailed && (
                <span className="cs-help" style={{ color: '#ff9b9b' }}>
                  Bake failed: {hqPrev?.message || 'unknown error'}
                </span>
              )}
              {hqErr && <span className="cs-help" style={{ color: 'var(--accent)' }}>{hqErr}</span>}
              <button
                className="cs-btn cs-btn-ghost cs-btn-sm"
                style={{ marginLeft: 'auto' }}
                onClick={bakeHqPreview}
                disabled={hqBusy || hqJobActive}
              >
                {hqJobActive ? 'Baking…' : hqReady || hqFailed ? '↻ Re-bake' : 'Bake now'}
              </button>
              <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setHqOpen(false)}>✕ Close</button>
            </div>
            {hqJobActive && (
              <div className="cs-hqprog">
                <div className="cs-progress">
                  <div
                    style={hqLive?.pct != null
                      ? { width: `${hqLive.pct}%` }
                      : { width: '30%', animation: 'cs-hqprog-slide 1.4s ease-in-out infinite alternate' }}
                  />
                </div>
                <button
                  className="cs-btn cs-btn-ghost cs-btn-sm"
                  onClick={() => setHqLogOpen((o) => !o)}
                  disabled={!hqLive?.log.length}
                  title="Show the worker's recent progress lines (ffmpeg time/fps/speed)"
                >
                  {hqLogOpen ? '▾ Log' : '▸ Log'}
                </button>
              </div>
            )}
            {hqJobActive && hqLogOpen && hqLive?.log.length > 0 && (
              <pre className="cs-hqlog">{hqLive.log.join('\n')}</pre>
            )}
            {hqReady && hqPrev.url && (
              <video src={hqPrev.url} controls playsInline className="cs-hqprev-video" />
            )}
          </div>
        )}

        <div className="cs-studio-bar">
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={togglePlay} disabled={!videoEl}>
            {playing ? '❚❚ Pause' : '► Play'}
          </button>
          <button
            className="cs-btn cs-btn-ghost cs-btn-sm"
            onClick={() => setViewMode((v) => (v === 'split' ? 'single' : 'split'))}
          >
            {viewMode === 'split' ? '⤢ Single view' : '⊞ Split view'}
          </button>
          <button
            className={`cs-btn cs-btn-sm ${showStrip ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
            onClick={() => setShowStrip((s) => !s)}
            title="Sample frames across this clip and apply your live grade to all of them — judge the look across the clip's full lighting range without rendering"
          >
            ▦ Frame strip
          </button>
          <button
            className={`cs-btn cs-btn-sm ${fullCheck ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
            onClick={grabFullRes}
            disabled={fullBusy || !curSeg || !!curSdkPrev}
            title={curSdkPrev
              ? 'Unavailable while the SDK de-log preview is active — the full-res master is flat log, so its color would contradict what you see.'
              : 'Grab the current frame from the full-resolution master source and apply your live grade — confirm color/detail at true resolution. Editing stays on the fast proxy.'}
          >
            {fullBusy ? 'Grabbing…' : '◉ Full-res check'}
          </button>
          {curSdkEligible && !curSdkPrev && (
            <button
              className="cs-btn cs-btn-ghost cs-btn-sm"
              onClick={buildSdkPreview}
              disabled={!movieId || sdkPrevQueued === curClipId}
              title="Decode this clip's window through its chosen RED SDK development (de-log base, EV, tone map, roll-off) into a preview proxy — the panes then show the TRUE bake color instead of flat log + a WebGL approximation. Takes a minute or two; it swaps in automatically."
            >
              {sdkPrevQueued === curClipId ? '⏳ SDK preview building…' : '◆ Build SDK preview'}
            </button>
          )}
          <button
            className={`cs-btn cs-btn-sm ${hqOpen || hqJobActive ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
            onClick={() => {
              if (hqJobActive || hqReady || hqFailed) setHqOpen((o) => !o);
              else bakeHqPreview();
            }}
            disabled={hqBusy || !curSeg}
            title="Server-bake this clip's trimmed segment at 1080p with the full look (clip + movie grades, keyframes) — watch a true-quality version of the edit before the full movie bake. Grade on the frame strip / fast proxy, then bake here when the look is settled."
          >
            {hqJobActive ? '⏳ Baking HQ…' : hqReady ? '🎞 HQ preview' : '🎞 Bake HQ preview'}
          </button>
          <div className="cs-layerchips">
            {layerRows.map((r) => (
              <button
                key={r.key}
                className={`cs-layerchip ${layers[r.key] ? 'on' : ''}`}
                title={`Toggle ${r.label} on the graded view`}
                onClick={() => setLayers((p) => ({ ...p, [r.key]: !p[r.key] }))}
              >
                <span className="cs-layerchip-box">{layers[r.key] ? '✓' : ''}</span>{r.label}
              </button>
            ))}
          </div>
          <span className="cs-help">Left = raw source · right builds up your enabled layers live.</span>
        </div>

        {showStrip && (
          <ClipFilmstrip
            url={curUrl}
            srcOff={curOff}
            streamType={curSeg?.streamType}
            inSec={curSeg?.inSec || 0}
            outSec={curSeg?.outEff || 0}
            speed={curSeg?.speed || 1}
            startInMovie={curSeg?.startInMovie || 0}
            clipKey={curClipId}
            grade={showGrade}
            baseGrade={showBase}
            onSeek={seekMovie}
            onFrames={(times) => setStripTimes({ clipId: curSegRef.current?.id, times })}
          />
        )}

        <div className="cs-clipscrub">
          <input
            className="cs-slider"
            type="range"
            min="0"
            max={Math.max(totalDur, 0.1)}
            step="0.05"
            value={Math.min(t, totalDur)}
            onChange={(e) => seekMovie(Number(e.target.value))}
          />
          <span className="cs-badge">{fmtTime(t)} / {fmtTime(totalDur)}</span>
        </div>
        {footer}
      </div>

      <div className="cs-studio-side">
        {/* Hidden via CSS (cs-side-off) rather than unmounted so panel state —
            expanded grade sections, editing target — survives the toggle. */}
        {onToggleSide && (
          <div className="cs-sidehead">
            <button
              type="button"
              className="cs-panelhide"
              title="Hide this panel — restore it with the ＋ tools chip in the timeline toolbar"
              aria-label="Hide grade tools panel"
              onClick={onToggleSide}
            >
              −
            </button>
          </div>
        )}
        {activeClip && (
          <ClipInspector
            clip={activeClip}
            onSpeed={(s) => onClipSpeed && onClipSpeed(activeClip.id, s)}
            title={`Selected clip · ${segIndex + 1}/${clips.length}${clipLabel ? ` · ${clipLabel}` : ''}`}
          />
        )}
        <div className="cs-gradetarget">
          <div className="cs-help" style={{ marginBottom: 6 }}>Editing</div>
          <div className="cs-segment">
            <button className={target === 'clip' ? 'on' : ''} onClick={() => setTarget('clip')}>
              This clip {clipGrade !== DEFAULT_GRADE && gradeSignature(clipGrade) !== IDENTITY_SIG ? '●' : ''}
            </button>
            <button className={target === 'movie' ? 'on' : ''} onClick={() => setTarget('movie')}>
              Whole movie {gradeSignature(movieG) !== IDENTITY_SIG ? '●' : ''}
            </button>
          </div>
          <div className="cs-help" style={{ marginTop: 6 }}>
            {target === 'clip'
              ? 'Per-clip look — baked into just this clip. Move the playhead to grade a different clip.'
              : 'Global look applied on top of every clip when published.'}
          </div>
        </div>

      {/* Look provenance for the active clip: where its grade came from and the
          deliberate actions available. Nothing is ever applied automatically —
          the asset's saved look is only OFFERED here (one explicit click). */}
      {target === 'clip' && activeClip && (() => {
        const hasOwn = !!activeClip.grade && gradeSignature(activeClip.grade) !== IDENTITY_SIG;
        const assetLook = activeClip.assetId ? assetLooks[activeClip.assetId] : null;
        // Keyframed looks come from the deliberate proof-strip promote, never
        // from the old silent asset-copy — treat them as set-in-this-movie.
        const keyframed = Array.isArray(activeClip.gradeKeyframes) && activeClip.gradeKeyframes.length > 0;
        if (hasOwn && activeClip.gradeLocked !== true && !keyframed && onClearClipLook) {
          return (
            <div className="cs-gradenote">
              <span>
                ⚠ This clip’s look was <strong>copied from its source asset</strong> automatically
                (older editor behavior) — if the grade is already baked into the file’s pixels,
                it’s being applied twice.
              </span>
              <button
                type="button"
                className="cs-linklike"
                title="Remove the copied look — this clip previews and publishes raw (grade cleared; the asset's own saved look is untouched)"
                onClick={() => onClearClipLook(activeClip.id)}
              >
                Remove the copied look →
              </button>
            </div>
          );
        }
        if (hasOwn && onClearClipLook) {
          return (
            <div className="cs-gradenote">
              <span>This clip’s look was <strong>set in this movie</strong> (yours).</span>
              <button
                type="button"
                className="cs-linklike"
                title="Clear this clip's look entirely — back to the raw, ungraded source"
                onClick={() => onClearClipLook(activeClip.id)}
              >
                Clear look →
              </button>
            </div>
          );
        }
        if (!hasOwn && assetLook && onApplyAssetLook) {
          return (
            <div className="cs-gradenote">
              <span>
                🎨 This clip’s source has a <strong>saved look</strong> that is <em>not</em> applied
                here — this clip currently previews and publishes raw.
              </span>
              <button
                type="button"
                className="cs-linklike"
                title="Deliberately copy the asset's saved color/light look onto this movie clip (then tweak it per-clip below). Nothing is ever applied without this click."
                onClick={() => onApplyAssetLook(activeClip.id)}
              >
                Asset look available — Apply →
              </button>
            </div>
          );
        }
        return null;
      })()}

      {/* When the OTHER layer is what's coloring the preview, say so — otherwise
          "Style: None" on this layer looks like a bug next to a graded image. */}
      {target === 'clip' && otherActive && (
          <div className="cs-gradenote">
            <span>
              🎬 A <strong>whole-movie look</strong> is active — that’s the color you see on the right.
              The styles below are just <em>this clip’s</em> own look (currently none on top).
            </span>
            <button type="button" className="cs-linklike" onClick={() => setTarget('movie')}>
              Edit / reset the whole-movie look →
            </button>
          </div>
        )}
        {target === 'movie' && otherActive && (
          <div className="cs-gradenote">
            <span>This clip also has its <strong>own look</strong> baked underneath this movie-wide grade.</span>
            <button type="button" className="cs-linklike" onClick={() => setTarget('clip')}>
              Edit this clip’s look →
            </button>
          </div>
        )}

        {/* Quick edit = presets + full panel. Guided stages = the photo editor's
            stage-by-stage pipeline (decode → wb → … → effects, no local masks)
            with per-stage auto suggestions — same fine-tuning, per clip. */}
        <div className="cs-gradetarget">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="cs-segment" style={{ flex: '1 1 auto' }}>
              <button className={panelMode === 'quick' ? 'on' : ''} onClick={() => setPanelMode('quick')}>
                Quick edit
              </button>
              <button className={panelMode === 'stages' ? 'on' : ''} onClick={() => setPanelMode('stages')}>
                Guided stages {stageLive.some(Boolean) ? '●' : ''}
              </button>
            </div>
            {onUndo && (
              <div style={{ display: 'flex', gap: 4, flex: '0 0 auto' }}>
                <button
                  className="cs-btn cs-btn-ghost cs-btn-sm"
                  title="Undo the last edit — grade, decode, stage or timeline change (⌘Z)"
                  aria-label="Undo"
                  onClick={onUndo}
                  disabled={!canUndo}
                >
                  ↶
                </button>
                <button
                  className="cs-btn cs-btn-ghost cs-btn-sm"
                  title="Redo (⌘⇧Z / ⌘Y)"
                  aria-label="Redo"
                  onClick={onRedo}
                  disabled={!canRedo}
                >
                  ↷
                </button>
              </div>
            )}
          </div>
          {stageMode && (
            <div className="cs-help" style={{ marginTop: 6 }}>
              The full adjustment tree, one decision at a time — the same 10-stage pipeline as the
              photo editor (local masks excluded: too heavy to bake across every frame). Left pane =
              through the previous stage · right = what this stage adds.
            </div>
          )}
        </div>

        {!stageMode && (
        <div className="cs-layerlist">
          {layerRows.map((r) => (
            <div key={r.key} className="cs-layerrow sel">
              <button
                className={`cs-toggle ${layers[r.key] ? 'on' : ''}`}
                title={`Show / hide ${r.label}`}
                onClick={() => setLayers((p) => ({ ...p, [r.key]: !p[r.key] }))}
              >
                <span className="cs-toggle-knob" />
              </button>
              <div className="cs-layername">
                <span className="cs-layertitle">{r.label}{r.live && <i className="cs-livedot" />}</span>
                <span className="cs-layerhint">{r.hint}</span>
              </div>
            </div>
          ))}
        </div>
        )}

        {!stageMode && (
        <div className="cs-autobar">
          <div className="cs-autobar-row">
            <button
              className="cs-btn cs-btn-primary cs-btn-sm"
              disabled={!origStats}
              title={origStats ? 'Analyze this frame and apply a balanced auto correction' : 'Waiting for the preview to render'}
              onClick={() => commit(autoGrade(origStats, editing, 'all'))}
            >
              ✨ Auto enhance
            </button>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!origStats} onClick={() => commit(autoGrade(origStats, editing, 'light'))}>Auto light</button>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!origStats} onClick={() => commit(autoGrade(origStats, editing, 'color'))}>Auto color</button>
            <button
              className="cs-btn cs-btn-ghost cs-btn-sm"
              title="Clear ALL grading back to the untouched original (keeps only the camera de-log for RED/log footage)"
              onClick={() => commit(neutralLook(editing))}
            >
              Reset
            </button>
          </div>
          <div className="cs-autobar-label">Styles</div>
          <div className="cs-autobar-styles">
            {PRESETS.map((p) => {
              // "None" = a TRUE reset to the ungraded original (decode-only for
              // log footage) — not the default look stacked on the decode, which
              // left a residual S-curve baked in.
              const isNone = p.id === 'none';
              const chipGrade = isNone ? neutralLook(editing) : applyLook(editing, p.grade);
              return (
                <button
                  key={p.id}
                  className={`cs-stylechip ${gradeSignature(editing) === gradeSignature(chipGrade) ? 'on' : ''}`}
                  title={isNone ? 'Remove the look entirely — back to the ungraded original' : `Apply the ${p.name} look`}
                  onClick={() => commit(isNone ? chipGrade : stampPreset(chipGrade, p))}
                >
                  {p.name}
                </button>
              );
            })}
          </div>
          <div className="cs-help" style={{ marginTop: 6 }}>
            Auto analyzes the current frame (exposure, contrast, white balance). Styles are themed
            looks — start from one, then fine-tune below. <b>None</b> removes the look entirely.
            Every edit is non-destructive (⌘Z undoes).
          </div>
        </div>
        )}

        {stageMode && (
          <div className="cs-autobar">
            <div className="cs-stagenav">
              {VIDEO_STAGES.map((s, i) => (
                <button
                  key={s.id}
                  className={`cs-stagechip ${i === stageIdx ? 'on' : ''}`}
                  title={s.goal}
                  onClick={() => setStageIdx(i)}
                >
                  <span className="cs-stagechip-n">{i + 1}</span>
                  {s.name}
                  {stageLive[i] ? <i className="cs-livedot" /> : null}
                </button>
              ))}
            </div>
            <div className="cs-stagegoal">
              <b>{stageDef.name}.</b>{' '}
              {stageDef.id === 'decode' && !isRedClip && !(editing?.inputTransform && editing.inputTransform !== 'srgb')
                // Truthful blurb for display-ready sources: the canonical goal
                // talks about de-logging a flat RED negative, which doesn't
                // apply to an already-graded Rec.709/sRGB upload.
                ? 'Declare what this footage is. This clip reads as display-ready (Rec.709/sRGB), so there’s nothing to de-log — keep "no transform" and move on, or pick a log curve below if the file is actually flat camera log.'
                : stageDef.goal}
            </div>
            <div className="cs-autobar-row" style={{ marginTop: 6 }}>
              <button
                className="cs-btn cs-btn-ghost cs-btn-sm"
                disabled={stageIdx === 0}
                onClick={() => setStageIdx((i) => Math.max(0, i - 1))}
              >
                ← {stageIdx > 0 ? VIDEO_STAGES[stageIdx - 1].name : ''}
              </button>
              <button
                className="cs-btn cs-btn-primary cs-btn-sm"
                disabled={!stageHasSug}
                title={stageHasSug ? 'Apply every auto suggestion for this stage (each is also shown under its slider below)' : 'No auto suggestions for this stage — dial it by eye'}
                onClick={applyStageSug}
              >
                ✨ Apply stage auto
              </button>
              <button
                className="cs-btn cs-btn-ghost cs-btn-sm"
                disabled={!stageLive[stageIdx]}
                title={stageLive[stageIdx] ? `Undo everything the ${stageDef.name} stage is doing (back to no-edit for this stage only)` : `${stageDef.name} isn't changing anything yet`}
                onClick={resetStage}
              >
                Reset stage
              </button>
              <button
                className="cs-btn cs-btn-ghost cs-btn-sm"
                disabled={stageIdx >= VIDEO_STAGES.length - 1}
                onClick={() => setStageIdx((i) => Math.min(VIDEO_STAGES.length - 1, i + 1))}
              >
                {stageIdx < VIDEO_STAGES.length - 1 ? VIDEO_STAGES[stageIdx + 1].name : ''} →
              </button>
            </div>
            {/* Reference frame match: pair this clip's keyframes with moments
                in a graded reference clip (e.g. the photog's delivery) — Grok
                then grades toward that look, sampled at the exact pairs. */}
            <ReferenceMatch
              tripId={curTripId}
              assetId={curAssetId}
              clipUrl={curUrl}
              seg={curSeg}
              savedRef={gradeRef}
              disabled={!videoEl}
            />
            {/* Cross-stage 🎯 Match reference pass: one request that may move a
                curated parameter set spanning the whole pipeline (raw + full
                current grade + paired reference frames + measured deltas), so
                Grok distributes the correction to the control that owns it —
                the per-stage assist below then stays for stage-scoped polish. */}
            {Array.isArray(gradeRef?.pairs) && gradeRef.pairs.length > 0 && (
              <div className="cs-matchref">
                <div className="cs-matchref-cap">
                  🎯 <b>Match reference</b> — one pass across <i>all</i> stages: Grok compares the raw
                  clip and your full current grade against the paired reference frames (plus measured
                  histogram deltas) and distributes corrections to the controls that own them.
                  Auto-refine iterates until it converges. Use the per-stage assist below for polish.
                </div>
                <StageAssist
                  stageDef={MATCH_REF_STAGE}
                  grade={editing}
                  onChange={commit}
                  capture={captureMatchFrames}
                  multiFrame
                  context={{ isRed: isRedClip, kind: 'video', inputTransform: editing?.inputTransform }}
                  disabled={!videoEl}
                />
              </div>
            )}
            {/* Grok stage assist: sends the actual before/after pane captures so
                the model judges what THIS stage adds — artistic suggestions on
                top of the histogram-measured auto chips above. */}
            <StageAssist
              stageDef={stageDef}
              stages={VIDEO_STAGES}
              grade={editing}
              onChange={commit}
              capture={captureStageFrames}
              multiFrame
              measurements={measurementLines(stageSugByPath)}
              context={{ isRed: isRedClip, kind: 'video', inputTransform: editing?.inputTransform }}
              disabled={!videoEl}
            />
            <div className="cs-help" style={{ marginTop: 6 }}>
              Suggestions analyze this stage’s <i>input</i> frame (the left pane) — scrub to a
              representative moment first. With <b>×3</b> on, Grok also sees frames sampled across the
              clip (the playhead hops briefly while capturing); with a saved <b>🎯 Reference match</b>,
              it samples the paired keyframes and grades toward the reference look. A <i className="cs-livedot" style={{ position: 'static', display: 'inline-block' }} /> dot
              marks stages currently doing something.
            </div>
          </div>
        )}

        {/* Controls ABOVE the scopes: in stage mode the stage's actual
            adjustments must sit right under the stage bar — buried below the
            histogram they read as "no controls" (the panel scrolls). */}
        <GradePanel
          grade={editing}
          onChange={commit}
          kind="video"
          isRed={isRedClip}
          sections={stageMode ? stageDef.sections : null}
          suggestions={stageMode ? stageSugByPath : null}
          presets={presets || []}
          onSavePreset={(name, g) => addGradePreset({ name, grade: g, kind: 'video' })}
          onDeletePreset={(pid) => deleteGradePreset(pid)}
        />

        <div>
          <div className="cs-scopes-label">Scopes ({stageMode ? `+ ${stageDef.name}` : target === 'movie' ? 'movie look' : 'this clip'})</div>
          <Scopes stats={stats} />
        </div>
      </div>
    </div>
  );
}
